import { SelectChangeEvent } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  STATUS_BADGE_TYPE,
  StatusBadgeType,
} from "../../../../../../../../../components";
import {
  BILLING_DETAILS_SITE_CORE_VALUE,
  BILLING_TYPE,
} from "../../../../../../../../../helpers/constants";
import {
  getBillingScheduleExecutionStatus,
  getValueOrBlank,
  getValueOrBlankArray,
  isActiveStatusAndImmediateBillingType,
  isBilledAndActivityBasedBillingType,
  isEmpty,
  isImmediateBilling,
  isNotEmpty,
  isOpenStatusAndImmediateBillingType,
  isStatusActive,
  isStatusClosed,
  isStatusConcon,
  isStatusInActive,
} from "../../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../../hooks/useSiteCore";
import { BILLING_TYPE_CLEAR_ACTIONS } from "../../../../../../../helper";
import { ProgramFeeTabFormKey } from "../../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../siteCore/SiteCoreKeys";
import { BillingTypeFieldInvoicePropsForProgram } from "./BillingTypeFieldInvoice";

const feeKey = `${ProgramFeeTabFormKey.fee}`;
const feeWithFormatKey = `${ProgramFeeTabFormKey.feeWithFormat}`;
const billingTriggerIdKey = `${ProgramFeeTabFormKey.billingTriggerId}`;
const billingSchedulesKey = `${ProgramFeeTabFormKey.billingSchedules}`;
const schedulePeriodKey = `${ProgramFeeTabFormKey.schedulePeriod}`;
const schedulesKey = `${ProgramFeeTabFormKey.schedules}`;
const feePercentageKeyInvoice = `${ProgramFeeTabFormKey.feePercentage}`;
const amountKeyInvoice = `${ProgramFeeTabFormKey.amount}`;
const amountWithFormatKeyInvoice = `${ProgramFeeTabFormKey.amountWithFormat}`;
const scheduleFrequencyKey = `${ProgramFeeTabFormKey.scheduleFrequency}`;
const feePaymentTypeKey = `${ProgramFeeTabFormKey.feePaymentType}`;
const milestonesKey = `${ProgramFeeTabFormKey.milestones}`;
const activitiesKey = `${ProgramFeeTabFormKey.activities}`;
const minimumTransferFeeKey = `${ProgramFeeTabFormKey.minimumTransferFee}`;
const minimumTransferFeeWithFormatKey = `${ProgramFeeTabFormKey.minimumTransferFeeWithFormat}`;
const earlyPayDaysKey = `${ProgramFeeTabFormKey.earlyPayDays}`;
const earlyPayPercentageKey = `${ProgramFeeTabFormKey.earlyPayPercentage}`;
const proBonoCategoryKey = `${ProgramFeeTabFormKey.proBonoCategoryId}`;
const specialBillingInstructionsKey = `${ProgramFeeTabFormKey.specialBillingInstructions}`;
const billingTypeKey = `${ProgramFeeTabFormKey.billingTypeId}`;

export const useBillingTypeFieldInvoice = (
  props: BillingTypeFieldInvoicePropsForProgram,
) => {
  const {
    currencyFormat,
    currencyId,
    defaultUserCurrency,
    currenciesList,
    forceUpdate,
    programTemplateId,
    pageId,
    setShowWarningOnBillingChange,
    programData,
    programFeeDetailsData,
  } = props;

  const { gridSizeMedium: gridSize } = useGridContainer();
  const { isTablet, isMobile } = useMediaQueries();
  const { commonARIALabel } = useNavigationSiteCore();

  const {
    control,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const isProBonoSelectedFirstRef = useRef(false);
  const [isProgramFeeDisabled, setIsProgramFeeDisabled] = useState(false);
  const [isBillingTypeDisabled, setIsBillingTypeDisabled] = useState(false);
  const [lastBillingType, setLastBillingType] = useState("");
  const [isChangeFirstTimeBillingType, setIsChangeFirstTimeBillingType] =
    useState(true);

  const {
    commonLabels,
    fetchDropdownValues,
    useFetchLabel,
    fetchValidationMessage,
    commonValidationMessages,
  } = useSiteCore();

  const {
    programFeeTitle,
    earlyPayDays,
    earlyPayPercentage,
    minimumTransferFee,
    billingTrigger,
    currency,
    programFee,
    billingTriggerList,
    billingTypeList,
    billingType: billingTypeSiteCore,
    proBonoCategory,
    proBonoCategoryList,
    specialBillingInstructions,
    earlyPayPercentageToolTipMessage,
  } = programSiteCoreKeys;

  const handleConfirmationStatusTrue = () => {
    setIsChangeFirstTimeBillingType(false);
    clearOnBillingTypeChangeInvoice();
  };

  const handleConfirmationStatusFalse = () => {
    setValue(billingTypeKey, lastBillingType);
  };

  const getLabel = useFetchLabel(programFeeTitle);

  const getDropdownValues = (key: string) => {
    return fetchDropdownValues(programFeeTitle, key);
  };

  const billingTypeWatch = useWatch({
    control,
    name: billingTypeKey,
  });

  const proBonoCategoryWatch = useWatch({
    control,
    name: proBonoCategoryKey,
  });

  const shouldIgnoreZeroValidationForFee = useMemo(
    () =>
      isNotEmpty(proBonoCategoryWatch) && isImmediateBilling(billingTypeWatch),
    [proBonoCategoryWatch, billingTypeWatch],
  );

  useEffect(() => {
    handleProBonoFieldChange(proBonoCategoryWatch);
  }, [proBonoCategoryWatch]);

  const programStatus = getValueOrBlank(
    programData?.programStatus,
    STATUS_BADGE_TYPE.open,
  ) as StatusBadgeType;
  const billingSchedules = getValues(billingSchedulesKey);

  const isAllSchedulesExecuted = useMemo(
    () => getBillingScheduleExecutionStatus(billingSchedules).allExecuted,
    [billingSchedules],
  );

  const isDisabledForActiveStatusAndImmediateBillingType = useMemo(() => {
    return (
      programFeeDetailsData?.isInvoiced &&
      (isActiveStatusAndImmediateBillingType(programStatus, billingTypeWatch) ||
        isOpenStatusAndImmediateBillingType(programStatus, billingTypeWatch))
    );
  }, [programStatus, programFeeDetailsData?.isInvoiced, billingTypeWatch]);

  // For Milestone based billing type, schedule based billing type, the billing type is not allowed to change if the program status is active.
  const isProgramStatusActiveAlready = useMemo(
    () =>
      isStatusClosed(programStatus) ||
      isStatusActive(programStatus) ||
      isStatusInActive(programStatus) ||
      isStatusConcon(programStatus),
    [programStatus],
  );

  const isActivityBasedBilled = useMemo(() => {
    return isBilledAndActivityBasedBillingType(
      billingTypeWatch,
      programFeeDetailsData?.isBilled,
    );
  }, [programFeeDetailsData?.isBilled, billingTypeWatch]);

  const clearMilestoneBasedValueInvoice = () => {
    setValue(milestonesKey, "");
    setValue(activitiesKey, "");
    setValue(feePaymentTypeKey, "");
    setValue(feePercentageKeyInvoice, "");
    setValue(amountKeyInvoice, "");
    setValue(amountWithFormatKeyInvoice, "");
    setValue(billingSchedulesKey, []);
  };

  const clearScheduleBasedValueInvoice = () => {
    setValue(schedulePeriodKey, "");
    setValue(schedulesKey, "");
    setValue(feePaymentTypeKey, "");
    setValue(feePercentageKeyInvoice, "");
    setValue(amountKeyInvoice, "");
    setValue(amountWithFormatKeyInvoice, "");
    setValue(scheduleFrequencyKey, "");
    setValue(billingSchedulesKey, []);
  };

  const clearActivityBasedValueInvoice = () => {
    setValue(billingTriggerIdKey, "");
  };

  const shouldDisableOtherFields = useMemo(() => {
    return (
      isDisabledForActiveStatusAndImmediateBillingType ||
      isAllSchedulesExecuted ||
      isActivityBasedBilled
    );
  }, [
    isDisabledForActiveStatusAndImmediateBillingType,
    isAllSchedulesExecuted,
    isActivityBasedBilled,
  ]);

  const shouldDisableProBonoField = useMemo(() => {
    return (
      isDisabledForActiveStatusAndImmediateBillingType ||
      (!isImmediateBilling(billingTypeWatch) && isNotEmpty(billingTypeWatch))
    );
  }, [isDisabledForActiveStatusAndImmediateBillingType, billingTypeWatch]);

  const shouldDisableProgramFee = useMemo(() => {
    return (
      isDisabledForActiveStatusAndImmediateBillingType ||
      isAllSchedulesExecuted ||
      isActivityBasedBilled ||
      isProgramFeeDisabled
    );
  }, [
    isDisabledForActiveStatusAndImmediateBillingType,
    isAllSchedulesExecuted,
    isActivityBasedBilled,
    isProgramFeeDisabled,
  ]);

  const shouldDisableBillingType = useMemo(() => {
    return (
      isDisabledForActiveStatusAndImmediateBillingType ||
      isProgramStatusActiveAlready ||
      isBillingTypeDisabled
    );
  }, [
    isDisabledForActiveStatusAndImmediateBillingType,
    isProgramStatusActiveAlready,
    isBillingTypeDisabled,
  ]);

  const handleProBonoFieldChange = (value: string) => {
    const { billingTypeId } = getValues();

    if (isNotEmpty(value)) {
      // ProBono category selected - set fee to 0 and disable program fee
      setValue(feeWithFormatKey, 0);
      setValue(feeKey, 0);
      setIsProgramFeeDisabled(true);

      if (isEmpty(billingTypeId)) {
        isProBonoSelectedFirstRef.current = true;
        // Case 2: ProBono selected first - set billing type to IMMEDIATE and disable billing type field
        setValue(
          billingTypeKey,
          BILLING_DETAILS_SITE_CORE_VALUE.billingTypeImmediateBilling,
        );
        setIsBillingTypeDisabled(true);
        if (errors[billingTypeKey]) {
          clearErrors(billingTypeKey);
        }

        if (errors[feeWithFormatKey]) {
          clearErrors(feeWithFormatKey);
        }
      }
    } else {
      // ProBono category cleared - reset fields and enable them
      if (isProBonoSelectedFirstRef.current) {
        setValue(billingTypeKey, "");
        setIsBillingTypeDisabled(false);
      }
      setValue(feeWithFormatKey, "");
      setIsProgramFeeDisabled(false);
    }

    forceUpdate();
  };

  const clearOnBillingTypeChangeInvoice = () => {
    const actions = BILLING_TYPE_CLEAR_ACTIONS[billingTypeWatch] ?? [];
    if (actions.includes(BILLING_TYPE.MILESTONE))
      clearMilestoneBasedValueInvoice();
    if (actions.includes(BILLING_TYPE.SCHEDULE))
      clearScheduleBasedValueInvoice();
    if (actions.includes(BILLING_TYPE.ACTIVITY))
      clearActivityBasedValueInvoice();
    forceUpdate();
  };

  useEffect(() => {
    if (!isImmediateBilling(billingTypeWatch)) {
      setValue(proBonoCategoryKey, "");
    }
    if (isEmpty(lastBillingType)) setLastBillingType(billingTypeWatch);
  }, [billingTypeWatch]);

  const checkAllowBillingToChangeInvoice = () => {
    const [billingSchedules, billingType] = getValueOrBlankArray(
      getValues([billingSchedulesKey, billingTypeKey]),
    );

    return (
      !isNotEmpty(billingSchedules) &&
      !(billingType && isChangeFirstTimeBillingType)
    );
  };

  const handleBillingTypeChange = (e: SelectChangeEvent<unknown>) => {
    let isPauseLastSelection = false;
    if (!checkAllowBillingToChangeInvoice() && isNotEmpty(billingTypeWatch)) {
      setShowWarningOnBillingChange(true);
      isPauseLastSelection = true;
    } else {
      clearOnBillingTypeChangeInvoice();
      setIsChangeFirstTimeBillingType(false);
    }
    if (!isPauseLastSelection) {
      const billingType = e.target.value as string;
      setLastBillingType(billingType);
    }

    forceUpdate();
  };

  return {
    // useFormContext
    control,
    setValue,

    // watch values
    billingTypeWatch,

    // sitecore funcs
    commonLabels,
    getDropdownValues,
    getLabel,
    fetchValidationMessage,
    commonValidationMessages,

    // props
    currencyFormat,
    currencyId,
    defaultUserCurrency,
    currenciesList,
    forceUpdate,
    programTemplateId,
    pageId,
    gridSize,
    isTablet,
    isMobile,
    commonARIALabel,

    // functions
    handleConfirmationStatusTrue,
    handleConfirmationStatusFalse,
    handleBillingTypeChange,

    // form keys
    minimumTransferFeeWithFormatKey,
    minimumTransferFeeKey,
    earlyPayPercentageKey,
    earlyPayDaysKey,
    proBonoCategoryKey,
    specialBillingInstructionsKey,
    billingTypeKey,
    feeKey,
    billingTriggerIdKey,
    feeWithFormatKey,

    // sitecore keys
    earlyPayPercentage,
    earlyPayDays,
    minimumTransferFee,
    currency,
    billingTrigger,
    programFee,
    billingTypeList,
    billingTriggerList,
    proBonoCategory,
    billingTypeSiteCore,
    specialBillingInstructions,
    proBonoCategoryList,
    earlyPayPercentageToolTipMessage,

    // other values
    shouldIgnoreZeroValidationForFee,
    isProgramStatusActiveAlready,
    shouldDisableProBonoField,
    shouldDisableProgramFee,
    shouldDisableBillingType,
    shouldDisableOtherFields,
  };
};
